export interface QuestGoalInput<T> {
  required: number;
  current: number;
  data: T;
}

export interface QuestGoal<T> extends QuestGoalInput<T> {
  complete: boolean;
}

export interface QuestProgress<T> {
  goals: ReadonlyArray<Readonly<QuestGoal<T>>>;
  completed: number;
  total: number;
  ratio: number;
  done: boolean;
}

export interface QuestProgressSummary {
  done: boolean;
  ratio: number;
}

export type QuestState = 'complete' | 'locked' | 'preparation' | 'active' | 'open';

export interface CardResultRecord {
  attempts: number;
  successes: number;
  bestMs: number | null;
  countedDays: ReadonlyArray<string>;
}

export interface CardResultOutcome extends CardResultRecord {
  counted: boolean;
}

export interface CardCapExemption {
  typen: ReadonlyArray<string>;
  material: ReadonlyArray<string>;
}

export interface ExerciseResultRecord {
  attempts: number;
  successes: number;
  lastAt: number;
  best: number | null;
  values: ReadonlyArray<number>;
  stamps: ReadonlyArray<number | null>;
}

function requiredCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) throw new RangeError('required must be positive');
  return value;
}

function currentCount(value: number): number {
  if (!Number.isFinite(value) || value < 0) throw new RangeError('current must not be negative');
  return value;
}

export function cardCapApplies(input: {
  exemption: CardCapExemption | null;
  exemptionDone: boolean;
  typ: string;
  symbol: string;
}): boolean {
  if (!input.exemption || input.exemptionDone) return true;
  return !(input.exemption.typen.includes(input.typ)
    && input.exemption.material.includes(input.symbol));
}

export interface GuidedStep {
  goalId: string;
  opened: boolean;
  done: boolean;
  released: boolean;
}

// The guided path of a new player: each step stays in focus from the moment it
// is offered until it is done, then the next offered step takes over. A step
// the player released stays released; a skill the player picked wins.
export function guidedFocus(input: {
  steps: ReadonlyArray<GuidedStep>;
  currentFocus: string | null;
}): string | null {
  if (input.currentFocus) {
    const current = input.steps.find(step => step.goalId === input.currentFocus);
    if (!current || !current.done) return input.currentFocus;
  }
  const next = input.steps.find(step => step.opened && !step.done && !step.released);
  return next ? next.goalId : null;
}

// The first goal stays in focus from the end of the overture until it is done,
// unless the player released it or picked another skill.
export function firstGoalFocus(input: {
  goalId: string;
  currentFocus: string | null;
  introDone: boolean;
  goalDone: boolean;
  released: boolean;
}): string | null {
  return guidedFocus({
    currentFocus: input.currentFocus,
    steps: [{
      goalId: input.goalId, opened: input.introDone, done: input.goalDone, released: input.released,
    }],
  });
}

// The first reward gets its own closing scene, once per profile, and only in
// the moment its goal is unlocked.
export function firstRewardMoment(input: {
  goalId: string;
  unlockedGoals: ReadonlyArray<string>;
  shown: boolean;
}): boolean {
  return !input.shown && input.unlockedGoals.includes(input.goalId);
}

export interface GuidedMoment {
  goalId: string;
  shown: boolean;
}

// The one-time transition of the guided path that is due now: the first step
// whose goal was just unlocked and whose card or scene was not shown yet.
export function guidedMoment(input: {
  moments: ReadonlyArray<GuidedMoment>;
  unlockedGoals: ReadonlyArray<string>;
}): string | null {
  const due = input.moments.find(moment => firstRewardMoment({
    goalId: moment.goalId, unlockedGoals: input.unlockedGoals, shown: moment.shown,
  }));
  return due ? due.goalId : null;
}

// What a goal adds on top of what the player already has, e.g. Dm, Em, D and A
// after C, G, Am and F.
export function newGoalMaterial(input: {
  material: ReadonlyArray<string>;
  known: ReadonlyArray<string>;
}): ReadonlyArray<string> {
  return Object.freeze(input.material.filter(item => !input.known.includes(item)));
}

export interface TempoGoal {
  goalId: string;
  exercises: ReadonlyArray<string>;
  minTempo: number | null;
  done: boolean;
}

// The tempo a timed goal starts with. At least its minimum; but unless the
// player picked a tempo, never so fast that the same run also counts for a
// later open goal on the same exercise (erste-takte at 90 BPM also finished
// kadenz-c from 80 BPM). A finished goal just keeps the old minimum rule.
export function goalStartTempo(input: {
  goal: TempoGoal;
  goals: ReadonlyArray<TempoGoal>;
  exercise: string;
  tempo: number;
  chosen: boolean;
}): number {
  const { goal, exercise, tempo } = input;
  if (!Number.isFinite(tempo) || tempo <= 0) throw new RangeError('tempo must be positive');
  if (goal.minTempo == null || !goal.exercises.includes(exercise)) return tempo;
  const min = goal.minTempo;
  if (input.chosen || goal.done) return Math.max(tempo, min);
  const ceiling = Math.min(...input.goals
    .filter(other => other.goalId !== goal.goalId && !other.done
      && other.minTempo != null && other.minTempo > min && other.exercises.includes(exercise))
    .map(other => other.minTempo as number));
  return tempo >= min && tempo < ceiling ? tempo : min;
}

export interface PracticeTally {
  total: number;
  correct: number;
  streak: number;
}

export type PracticeEvent = 'hit' | 'skip';

// Counts finished attempts only: showing a chord is not an attempt.
export function practiceTally(
  current: PracticeTally,
  event: PracticeEvent,
): Readonly<PracticeTally> {
  const hit = event === 'hit';
  return Object.freeze({
    total: current.total + 1,
    correct: current.correct + (hit ? 1 : 0),
    streak: hit ? current.streak + 1 : current.streak,
  });
}

export interface InputSourceNotes {
  countsAsPractice: boolean;
  notes: Iterable<number>;
}

// A chord counts as played only when every target note is held by a source
// that counts as practice (a real instrument). Notes from other sources, such
// as the computer keyboard, never complete it, not even together with MIDI.
export function heldFromPractice(
  targets: Iterable<number>,
  sources: ReadonlyArray<InputSourceNotes>,
): boolean {
  const wanted = [...targets];
  if (!wanted.length) return false;
  const held = new Set<number>();
  for (const source of sources) {
    if (source.countsAsPractice) for (const note of source.notes) held.add(note);
  }
  return wanted.every(note => held.has(note));
}

// The input facade of the app (`Eingabe` in js/input.js) as a port: which
// sources exist, whether each counts as practice, which notes it holds now.
export interface PracticeInputPort {
  liste(): ReadonlyArray<{ id: string; zaehltAlsUeben: boolean }>;
  noten(id: string): Iterable<number>;
}

// The one adapter every scoring path uses (progression, play-along, EXP):
// collects the notes of every input source and applies `heldFromPractice`.
export function heldFromInput(
  targets: Iterable<number>,
  input: PracticeInputPort,
): boolean {
  return heldFromPractice(targets, input.liste().map(source => ({
    countsAsPractice: source.zaehltAlsUeben,
    notes: input.noten(source.id),
  })));
}

// One wording for every place that tells a player why trial input (the
// computer keyboard) books nothing. Only the instrument counts as practice.
export const PRACTICE_SOURCE_TEXTS = Object.freeze({
  before: 'Fortschritt zählt nur am MIDI-Keyboard — die Computertastatur ist zum Ausprobieren.',
  playing: 'Du spielst auf der Computertastatur: gut zum Ausprobieren, Fortschritt zählt nur am MIDI-Keyboard.',
  round: 'Runde auf der Computertastatur — zählt nur am MIDI-Keyboard.',
  chip: 'Die Computertastatur zählt nicht als Üben — MIDI-Keyboard anschließen und hier tippen.',
  session: 'Übezeit zählt nur am MIDI-Keyboard — die Computertastatur ist zum Ausprobieren.',
});

export type PracticeSourceNoticeKind = 'none' | 'before' | 'playing';

export interface PracticeSourceNotice {
  kind: PracticeSourceNoticeKind;
  text: string;
}

// Where playing books progress, a player sees before playing that only the
// instrument counts (no instrument connected yet) and, once they play on a
// trial source there, that this playing books nothing. Connected and played
// only on the instrument: nothing to say.
export function practiceSourceNotice(input: {
  countsInPlace: boolean;
  practiceSourceConnected: boolean;
  trialSourcePlayed: boolean;
}): Readonly<PracticeSourceNotice> {
  let kind: PracticeSourceNoticeKind = 'none';
  if (input.countsInPlace) {
    if (input.trialSourcePlayed) kind = 'playing';
    else if (!input.practiceSourceConnected) kind = 'before';
  }
  return Object.freeze({ kind, text: kind === 'none' ? '' : PRACTICE_SOURCE_TEXTS[kind] });
}

// A finished round that did not count gets the reason when trial input was
// played in it; a clean round or one missed on the instrument keeps its text.
export function trialRoundNote(input: {
  countedFully: boolean;
  trialSourcePlayed: boolean;
}): string | null {
  return !input.countedFully && input.trialSourcePlayed ? PRACTICE_SOURCE_TEXTS.round : null;
}

export interface RunTally {
  bars: number;
  hits: number;
}

export interface ShownRunTally extends RunTally {
  finished: boolean;
}

// What the play-along status line shows: the running round once one of its
// bars is booked; right after a round, until the next bar is booked, the
// finished round instead of an empty "0 of 0".
export function shownRunTally(
  current: RunTally | null,
  lastRound: RunTally | null,
): ShownRunTally | null {
  if (!current) return null;
  if (current.bars > 0) return Object.freeze({ ...current, finished: false });
  if (lastRound && lastRound.bars > 0) return Object.freeze({ ...lastRound, finished: true });
  return null;
}

export interface PlayAlongRound extends RunTally {
  offsetsMs: ReadonlyArray<number>;
}

export interface PlayAlongRoundSummary {
  flawless: boolean;
  meanAbsOffsetMs: number | null;
}

// A play-along round is judged on its own bars and hits only: flawless means
// every bar of the sequence was hit, and the timing mean covers just the hits
// of this round. The host starts the next round with no bars, hits or offsets,
// so a sloppy round never colours the stamps of a later clean one.
export function playAlongRoundSummary(
  round: PlayAlongRound,
  barsInSequence: number,
): Readonly<PlayAlongRoundSummary> {
  const offsets = round.offsetsMs.filter(offset => Number.isFinite(offset)).map(Math.abs);
  const meanAbsOffsetMs = offsets.length
    ? offsets.reduce((sum, offset) => sum + offset, 0) / offsets.length
    : null;
  const flawless = round.bars > 0 && round.hits === round.bars && round.bars === barsInSequence;
  return Object.freeze({ flawless, meanAbsOffsetMs });
}

export function rewardOrder(
  pool: ReadonlyArray<string>,
  chosen: string,
): string[] {
  if (!pool.includes(chosen)) return [...pool];
  return [chosen, ...pool.filter(item => item !== chosen)];
}

export function evaluateGoals<T>(inputs: ReadonlyArray<QuestGoalInput<T>>): QuestProgress<T> {
  const goals = inputs.map(input => {
    const required = requiredCount(input.required);
    const current = Math.min(required, currentCount(input.current));
    return Object.freeze({ required, current, complete: current >= required, data: input.data });
  });
  const completed = goals.filter(goal => goal.complete).length;
  const required = goals.reduce((sum, goal) => sum + goal.required, 0);
  const current = goals.reduce((sum, goal) => sum + goal.current, 0);
  return Object.freeze({
    goals: Object.freeze(goals),
    completed,
    total: goals.length,
    ratio: required ? current / required : 0,
    done: goals.length > 0 && completed === goals.length,
  });
}

export function recordCardResult(
  record: CardResultRecord | undefined,
  result: { correct: boolean; elapsedMs: number; timeLimitMs: number | null; dayKey: string | null },
): Readonly<CardResultOutcome> {
  if (!Number.isFinite(result.elapsedMs) || result.elapsedMs < 0) {
    throw new RangeError('elapsedMs must not be negative');
  }
  if (result.timeLimitMs != null && (!Number.isFinite(result.timeLimitMs) || result.timeLimitMs < 0)) {
    throw new RangeError('timeLimitMs must not be negative');
  }
  const current = record ?? { attempts: 0, successes: 0, bestMs: null, countedDays: [] };
  const countedDays = Array.isArray(current.countedDays) ? [...current.countedDays] : [];
  const inTime = result.correct && (!result.timeLimitMs || result.elapsedMs <= result.timeLimitMs);
  const counted = inTime && (result.dayKey == null || !countedDays.includes(result.dayKey));
  if (counted && result.dayKey != null) {
    countedDays.push(result.dayKey);
    if (countedDays.length > 60) countedDays.splice(0, countedDays.length - 60);
  }
  return Object.freeze({
    attempts: current.attempts + 1,
    successes: current.successes + (counted ? 1 : 0),
    bestMs: inTime && (current.bestMs == null || result.elapsedMs < current.bestMs)
      ? result.elapsedMs : current.bestMs,
    countedDays: Object.freeze(countedDays),
    counted,
  });
}

export function recordExerciseResult(
  record: ExerciseResultRecord | undefined,
  result: {
    correct: boolean;
    value: number | null;
    recordedAt: number;
    stamps: number | null;
    historyLimit?: number;
  },
): Readonly<ExerciseResultRecord> {
  if (!Number.isFinite(result.recordedAt) || result.recordedAt < 0) {
    throw new RangeError('recordedAt must not be negative');
  }
  if (result.value != null && !Number.isFinite(result.value)) {
    throw new RangeError('value must be finite');
  }
  const historyLimit = result.historyLimit ?? 50;
  if (!Number.isInteger(historyLimit) || historyLimit <= 0) {
    throw new RangeError('historyLimit must be a positive integer');
  }
  const current = record ?? {
    attempts: 0, successes: 0, lastAt: 0, best: null, values: [], stamps: [],
  };
  const values = [...current.values];
  const stamps = [...(current.stamps || [])];
  while (stamps.length < values.length) stamps.push(null);
  if (stamps.length > values.length) stamps.splice(0, stamps.length - values.length);
  if (result.correct && result.value != null) {
    values.push(result.value);
    stamps.push(result.stamps ?? null);
    if (values.length > historyLimit) values.splice(0, values.length - historyLimit);
    if (stamps.length > historyLimit) stamps.splice(0, stamps.length - historyLimit);
  }
  return Object.freeze({
    attempts: current.attempts + 1,
    successes: current.successes + (result.correct ? 1 : 0),
    lastAt: result.correct ? result.recordedAt : current.lastAt,
    best: result.correct && result.value != null && (current.best == null || result.value > current.best)
      ? result.value : current.best,
    values: Object.freeze(values),
    stamps: Object.freeze(stamps),
  });
}

export function countExerciseSuccesses(
  record: ExerciseResultRecord | undefined,
  minValue: number | null,
  minStamps: number | null = null,
): number {
  if (!record) return 0;
  if (minStamps == null) {
    if (minValue == null) return record.successes;
    return record.values.filter(value => value >= minValue).length;
  }
  if (!record.values.length && record.successes > 0) return record.successes;
  return record.values.filter((value, index) => {
    const valueOk = minValue == null || value >= minValue;
    const stamp = record.stamps?.[index];
    return valueOk && (stamp == null || stamp >= minStamps);
  }).length;
}

export function bestExerciseValue(record: ExerciseResultRecord | undefined): number | null {
  return record?.best ?? null;
}

export function questState(
  progress: QuestProgressSummary,
  prerequisitesComplete: boolean,
  measurable: boolean,
): QuestState {
  if (progress.done) return 'complete';
  if (!prerequisitesComplete) return 'locked';
  if (!measurable) return 'preparation';
  return progress.ratio > 0 ? 'active' : 'open';
}

export function evaluateGroup(progress: ReadonlyArray<QuestProgressSummary>) {
  const completed = progress.filter(item => item.done).length;
  return Object.freeze({
    completed,
    total: progress.length,
    ratio: progress.length ? progress.reduce((sum, item) => sum + item.ratio, 0) / progress.length : 0,
    done: progress.length > 0 && completed === progress.length,
  });
}
